import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { unsubscribe } from '../../../decorators/unsubscribe.decorator';
import { ToasterService } from '../../../services/toaster.service';
import { UtilityService } from '../../../services/utility.service';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { SeoService } from '../../../services/seo.service';
import { pageSeoInfo } from '../../../constants/page-seo-info';
import { RadioInputComponent } from '../../../components/utility/form/radio-input/radio-input.component';
import { CheckboxInputComponent } from '../../../components/utility/form/checkbox-input/checkbox-input.component';
import { routes } from '../../../constants/routes';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogActionComponent } from '../../../components/utility/confirmation-dialog-action/confirmation-dialog-action.component';
import { MergeSuccessDialogComponent } from '../../../components/utility/merge-success-dialog/merge-success-dialog.component';

@Component({
  selector: 'app-multiple-email-sign-up',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RadioInputComponent,
    CheckboxInputComponent,
    RouterLink,
  ],
  templateUrl: './multiple-email-sign-up.component.html',
  styleUrl: './multiple-email-sign-up.component.scss',
})
@unsubscribe()
export class MultipleEmailSignUpComponent implements OnInit, OnDestroy {
  public emailSelectionForm!: FormGroup;
  public userDetails: any;
  public multipleEmails: {
    email: string;
    provider: string;
    id: string;
  }[] = [];
  selectedEmail: {
    email: string;
    provider: string;
    id: string;
  } | null = null;
  routes = routes;
  encodedProfile: string | null = null;

  constructor(
    private router: Router,
    public toaster: ToasterService,
    private apiService: ApiService,
    private authService: AuthService,
    public utilityService: UtilityService,
    private activatedRoute: ActivatedRoute,
    private seoService: SeoService,
    private matDialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.initEmailSelectionForm();
    this.encodedProfile =
      this.activatedRoute.snapshot.queryParams['profileDetails'];
    if (this.encodedProfile) {
      try {
        const decoded = decodeURIComponent(atob(this.encodedProfile));
        this.userDetails = JSON.parse(decoded);
        this.multipleEmails = this.userDetails?.multipleEmails || [];
      } catch {
        this.toaster.error('Failed to load profile details');
      }
    }
    const { title, description, canonicalUrl } = pageSeoInfo['profile'];
    this.seoService.setPageTitle(title);
    this.seoService.setPageDescription(description);
    this.seoService.setCanonicalUrl(canonicalUrl);
  }

  private initEmailSelectionForm(): void {
    this.emailSelectionForm = new FormGroup({
      selectedEmail: new FormControl('', [Validators.required]),
      isConfirmed: new FormControl(false, [Validators.requiredTrue]),
    });
  }

  onEmailSelection(email: {
    email: string;
    provider: string;
    id: string;
  }): void {
    this.selectedEmail = email;
    this.emailSelectionForm.patchValue({
      selectedEmail: email.email,
    });
  }

  onConfirmSelection(): void {
    if (!this.emailSelectionForm.get('isConfirmed')?.value) {
      this.toaster.error('Please confirm the selection');
      return;
    }

    if (!this.selectedEmail) {
      this.toaster.error('Please select an email');
      return;
    }

    this.matDialog
      .open(ConfirmationDialogActionComponent, {
        data: {
          dialogIcon: 'images/email-icon.svg',
          iconAlt: 'Email',
          iconTitle: 'Confirm Your Choice',
          headerText: 'Confirm Your Choice',
          message: `<strong>${this.selectedEmail.email}</strong> from your ${this.selectedEmail.provider} account will be your login email going forward. You can update this later in your profile settings.`,
          viewAsinnerHTML: true,
          primaryButtonText: 'Yes, Confirm',
          secondaryButtonText: 'Go Back',
          primaryButtonClass: 'btn-secondary',
          secondaryButtonClass: 'btn-bordered-black',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.updateEmail();
        }
      });
  }

  updateEmail() {
    this.apiService
      .mergeProfileByEmail({
        id: this.selectedEmail.id,
        parichayId: this.userDetails?.janParichayId,
        email: this.selectedEmail.email,
      })
      .subscribe({
        next: (response) => {
          if (response?.token) {
            this.matDialog
              .open(MergeSuccessDialogComponent, {
                data: {
                  name: response?.name || this.userDetails?.name || '',
                  email: this.selectedEmail?.email || response?.email || '',
                  phone: response?.phone || this.userDetails?.phone || '',
                  provider: 'Jan Parichay',
                },
                disableClose: true,
                panelClass: 'merge-success-panel',
              })
              .afterClosed()
              .subscribe(() => {
                const userData = {
                  email: this.selectedEmail?.email || response?.email || '',
                  phone: response?.phone || this.userDetails?.phone || '',
                };
                const queryParams = this.userDetails?.isLoggedInUser
                  ? {
                      isDetailsVerifiedAndMatch: 'true',
                      data: btoa(encodeURIComponent(JSON.stringify(userData))),
                    }
                  : {};
                this.authService.saveUserProfile(response);
                this.authService.redirectToDashboard(queryParams);
              });
          } else {
            const encodedData = btoa(
              encodeURIComponent(JSON.stringify(response)),
            );
            this.router.navigate(
              ['/', routes.account, routes.signUp, routes.myProfile],
              {
                replaceUrl: true,
                queryParams: {
                  data: encodedData,
                  success: 'y',
                  isMultipleEmail: true,
                },
              },
            );
          }
        },
        error: (error) => {
          const errorMsg =
            error instanceof Error ? error.message : 'Something went wrong';
          this.toaster.error(errorMsg);
        },
      });
  }

  onGoBack(): void {
    this.router.navigate(['/register']);
  }

  onSkipSelection(): void {
    this.authService.redirectToDashboard();
  }

  get selectedEmailControl() {
    return this.emailSelectionForm.get('selectedEmail') as FormControl;
  }

  get isConfirmedControl() {
    return this.emailSelectionForm.get('isConfirmed') as FormControl;
  }

  ngOnDestroy(): void {}
}
